import { useSearchParams } from 'react-router';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { API_URL } from '@/config/api';
import { useAgentConsentContext } from '@/hooks/react-query/useAgentConsentContext';
import type { AgentConsentParams } from '@/services/agentAuthService';
import dffLogo from '@/assets/dff-logo-blue.svg';

const CONSENT_ACTION = `${API_URL}/oauth/authorize/consent`;

/**
 * OAuth consent screen for agent (MCP) clients. Reached by a redirect from
 * GET /oauth/authorize; wrapped in ProtectedRoute so the staff user is logged in.
 */
const AgentConsentPage = () => {
  const [searchParams] = useSearchParams();

  const params: AgentConsentParams = {
    response_type: searchParams.get('response_type') ?? 'code',
    client_id: searchParams.get('client_id') ?? '',
    redirect_uri: searchParams.get('redirect_uri') ?? '',
    code_challenge: searchParams.get('code_challenge') ?? '',
    code_challenge_method: searchParams.get('code_challenge_method') ?? 'S256',
    scope: searchParams.get('scope') ?? undefined,
    state: searchParams.get('state') ?? undefined,
  };

  const { data, isLoading, isError, error } = useAgentConsentContext(params);

  return (
    <div className="min-h-screen bg-background flex flex-col justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md mb-6 flex justify-center items-center gap-2">
        <img src={dffLogo} alt="DFF" className="h-12" />
        <span className="text-3xl font-bold text-blue-900 dark:text-foreground">
          Flow
        </span>
      </div>

      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-card border border-border rounded-2xl shadow-sm px-6 py-8 sm:px-10">
          <h1 className="text-xl font-semibold text-foreground">
            Authorize agent access
          </h1>
          <p className="mt-2 text-sm text-muted-foreground">
            An agent client is requesting a non-human credential that will act
            on your behalf in your organization.
          </p>

          {isLoading && (
            <div className="mt-8 flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading request…
            </div>
          )}

          {isError && (
            <p className="mt-8 text-sm text-destructive">
              This authorization request is invalid or has expired
              {error?.message ? ` (${error.message})` : ''}.
            </p>
          )}

          {data && (
            <>
              <dl className="mt-6 grid grid-cols-[max-content_1fr] gap-x-4 gap-y-2 text-sm">
                <dt className="text-muted-foreground">Client</dt>
                <dd className="font-mono text-foreground break-all">
                  {data.client_id}
                </dd>
                <dt className="text-muted-foreground">Scope</dt>
                <dd className="font-mono text-foreground break-all">
                  {data.scope}
                </dd>
                <dt className="text-muted-foreground">Authorizing user</dt>
                <dd className="text-foreground break-all">
                  {data.authorizing_user_email}
                </dd>
              </dl>

              <form
                method="post"
                action={CONSENT_ACTION}
                className="mt-8 flex gap-3"
              >
                <input type="hidden" name="client_id" value={data.client_id} />
                <input
                  type="hidden"
                  name="redirect_uri"
                  value={data.redirect_uri}
                />
                <input type="hidden" name="scope" value={data.scope} />
                <input
                  type="hidden"
                  name="code_challenge"
                  value={data.code_challenge}
                />
                <input
                  type="hidden"
                  name="code_challenge_method"
                  value={data.code_challenge_method}
                />
                {data.state !== null && (
                  <input type="hidden" name="state" value={data.state} />
                )}
                <input
                  type="hidden"
                  name="consent_token"
                  value={data.consent_token}
                />
                <Button type="submit" name="decision" value="approve">
                  Approve
                </Button>
                <Button
                  type="submit"
                  name="decision"
                  value="deny"
                  variant="outline"
                >
                  Deny
                </Button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AgentConsentPage;
